import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { get, post } from "../../api/client";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { SelectField, TextField } from "../../components/ui/Field";
import { InlineError, Loading } from "../../components/ui/misc";

type Target = { membershipId: string; name: string };

export function TransferDialog({ callId, open, onOpenChange, onStarted }: { callId: string; open: boolean; onOpenChange: (o: boolean) => void; onStarted: () => void }) {
  const [type, setType] = useState<"BLIND" | "ATTENDED">("ATTENDED");
  const [kind, setKind] = useState<"member" | "number">("member");
  const [membershipId, setMembershipId] = useState("");
  const [number, setNumber] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const targets = useQuery({
    queryKey: ["voice", "transfer-targets"],
    queryFn: () => get<{ items: Target[] }>("/voice/transfer-targets").then((r) => r.items),
    enabled: open,
    refetchInterval: open ? 10_000 : false,
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post(`/calls/${callId}/transfer`, { type, target: kind === "member" ? { kind, membershipId } : { kind, number } });
      onStarted();
      onOpenChange(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Transfer call" description="The caller hears hold music while the transfer connects.">
      <form onSubmit={submit} className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-default">Transfer type</legend>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" name="type" checked={type === "ATTENDED"} onChange={() => setType("ATTENDED")} className="mt-1 accent-accent-600" />
            <span><span className="font-medium">Consult first (attended)</span><span className="block text-xs text-subtle">Talk to the recipient, then complete or cancel.</span></span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" name="type" checked={type === "BLIND"} onChange={() => setType("BLIND")} className="mt-1 accent-accent-600" />
            <span><span className="font-medium">Transfer now (blind)</span><span className="block text-xs text-subtle">You leave immediately. If nobody answers, the call rings you back.</span></span>
          </label>
        </fieldset>
        <SelectField label="Transfer to" value={kind} onChange={(e) => setKind(e.target.value as "member" | "number")}>
          <option value="member">A colleague</option>
          <option value="number">An external number</option>
        </SelectField>
        {kind === "member" ? (
          targets.isLoading ? <Loading label="Finding available colleagues" /> : (
            <SelectField label="Colleague" value={membershipId} onChange={(e) => setMembershipId(e.target.value)} required
              hint={targets.data?.length ? "Only colleagues who are available right now are listed." : undefined}>
              <option value="" disabled>{targets.data?.length ? "Select a colleague" : "No colleagues are available"}</option>
              {targets.data?.map((t) => <option key={t.membershipId} value={t.membershipId}>{t.name}</option>)}
            </SelectField>
          )
        ) : (
          <TextField label="Phone number" type="tel" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="+1 201 555 0123" required
            hint="Must be allowed by your organization's calling policy." />
        )}
        <InlineError error={error} />
        <div className="flex justify-end gap-2">
          <Button onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={kind === "member" && !membershipId}>Start transfer</Button>
        </div>
      </form>
    </Dialog>
  );
}
